(() => {
  const carouselSelector = "[data-loose-gems-carousel]";
  const filterFormSelector = "[data-loose-gems-filters]";
  const allFilterInputSelector = 'input[type="checkbox"][name^="filter."]';
  const filterInputSelector = `${allFilterInputSelector}:not([data-loose-gems-range-value])`;
  const delegatedFilterInputSelector = `${filterFormSelector} ${filterInputSelector}`;
  const rangeGroupSelector = "[data-loose-gems-range-group]";
  const rangeInputSelector = "[data-loose-gems-range-input]";
  const rangeSliderSelector = "[data-loose-gems-range-slider]";
  const rangeControlSelector = `${rangeInputSelector}, ${rangeSliderSelector}`;
  const rangeValueSelector = "input[data-loose-gems-range-value]";
  const rangeInputCommitDelay = 2000;
  const rangeInputCommitTimers = new WeakMap();
  const rangeStoragePrefix = "loose-gems-range:";
  const accordionSelector = "[data-loose-gems-mobile-accordion]";
  const accordionToggleSelector = "[data-loose-gems-accordion-toggle]";
  const selectedItemSelector = ".loose-gems-filter-carousel__item, .loose-gems-filter-values__item";
  /* Shopify filters take several values at once - that is what repeating the same parameter in
     the URL means - so a panel marked data-loose-gems-multi-select keeps every box the shopper
     ticks. Gem Type, Color and Shape are all set that way in snippets/loose-gems-filters.liquid.
     The range panels were never affected: their checkboxes are plumbing the sliders write to, and
     filterInputSelector already excludes them.
     Each of those three also has to be set to OR in Search & Discovery. Under AND, Shopify asks
     for stones matching every tick at once, which is merely narrow for color and impossible for
     shape - a stone has one cut, so two shapes returned nothing at all. */
  const multiSelectPanelSelector = "[data-loose-gems-multi-select]";

  const updateCarouselButtons = (carousel) => {
    const panel = carousel.closest("[data-loose-gems-filter-panel]");
    if (!panel) return;

    const prevButton = panel.querySelector("[data-loose-gems-carousel-prev]");
    const nextButton = panel.querySelector("[data-loose-gems-carousel-next]");
    if (!prevButton || !nextButton) return;

    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    const atStart = carousel.scrollLeft <= 2;
    const atEnd = carousel.scrollLeft >= maxScroll - 2;

    prevButton.disabled = atStart;
    nextButton.disabled = maxScroll <= 2 || atEnd;
  };

  const initCarousel = (carousel) => {
    if (carousel.dataset.looseGemsCarouselInitialized === "true") {
      updateCarouselButtons(carousel);
      return;
    }

    carousel.dataset.looseGemsCarouselInitialized = "true";
    const panel = carousel.closest("[data-loose-gems-filter-panel]");
    if (!panel) return;

    const prevButton = panel.querySelector("[data-loose-gems-carousel-prev]");
    const nextButton = panel.querySelector("[data-loose-gems-carousel-next]");

    const scrollCarousel = (direction) => {
      carousel.scrollBy({
        left: direction * Math.max(carousel.clientWidth * 0.75, 160),
        behavior: "smooth",
      });
    };

    prevButton?.addEventListener("click", () => scrollCarousel(-1));
    nextButton?.addEventListener("click", () => scrollCarousel(1));

    let ticking = false;
    carousel.addEventListener("scroll", () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateCarouselButtons(carousel);
        ticking = false;
      });
    });

    window.addEventListener("resize", () => updateCarouselButtons(carousel));
    updateCarouselButtons(carousel);
  };

  const syncPanelState = (panel) => {
    if (!panel) return;

    const hasActiveValue = Array.from(panel.querySelectorAll(allFilterInputSelector)).some((panelInput) => panelInput.checked);
    panel.classList.toggle("is-active", hasActiveValue);

    const clearItem = panel.querySelector(".loose-gems-filter-values__item--clear");
    if (clearItem) clearItem.classList.toggle("is-selected", !hasActiveValue);
  };

  const syncInputState = (input) => {
    const item = input.closest(selectedItemSelector);
    if (item) item.classList.toggle("is-selected", input.checked);

    syncPanelState(input.closest("[data-loose-gems-filter-panel]"));
  };

  const enforceSingleSelection = (input) => {
    if (!input.matches(delegatedFilterInputSelector)) return;

    const panel = input.closest("[data-loose-gems-filter-panel]");
    if (!panel) {
      syncInputState(input);
      return;
    }

    if (input.checked && !panel.matches(multiSelectPanelSelector)) {
      panel.querySelectorAll(filterInputSelector).forEach((peerInput) => {
        if (peerInput === input || peerInput.name !== input.name || !peerInput.checked) return;
        peerInput.checked = false;
        syncInputState(peerInput);
      });
    }

    syncInputState(input);
  };

  const normalizeSingleSelection = (root = document) => {
    root.querySelectorAll(filterFormSelector).forEach((form) => {
      const groups = new Map();

      form.querySelectorAll(filterInputSelector).forEach((input) => {
        const panel = input.closest("[data-loose-gems-filter-panel]");
        const panelKey = panel?.dataset.looseGemsFilterPanel || "root";
        const groupKey = `${panelKey}::${input.name}`;

        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey).push(input);
      });

      groups.forEach((inputs) => {
        const inMultiSelectPanel = inputs[0]?.closest(multiSelectPanelSelector);
        const checkedInputs = inputs.filter((input) => input.checked);

        if (!inMultiSelectPanel) {
          checkedInputs.forEach((input, index) => {
            if (index > 0) input.checked = false;
          });
        }

        inputs.forEach(syncInputState);
      });
    });
  };

  const toNumber = (value) => {
    if (value === "" || value === null || typeof value === "undefined") return NaN;
    return Number(value);
  };

  const formatRangeValue = (value) => {
    if (!Number.isFinite(value)) return "";
    return String(Number(value.toFixed(4)));
  };

  const getRangeStorageKey = (group) => {
    const valueInput = group.querySelector(rangeValueSelector);
    if (!valueInput?.name) return null;

    return `${rangeStoragePrefix}${window.location.pathname}:${valueInput.name}`;
  };

  const getStoredRangeSelection = (group) => {
    const key = getRangeStorageKey(group);
    if (!key) return null;

    try {
      const storedValue = window.sessionStorage.getItem(key);
      return storedValue ? JSON.parse(storedValue) : null;
    } catch (error) {
      return null;
    }
  };

  const clearStoredRangeSelection = (group) => {
    const key = getRangeStorageKey(group);
    if (!key) return;

    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Filters still work without preserving the typed display value.
    }
  };

  const storeRangeSelection = (group, selection) => {
    const key = getRangeStorageKey(group);
    if (!key || !selection) return;

    if (selection.allowsEmpty && selection.minRaw === "" && selection.maxRaw === "") {
      clearStoredRangeSelection(group);
      return;
    }

    const storedSelection = {
      maxRaw: selection.allowsEmpty && selection.maxRaw === "" ? "" : formatRangeValue(selection.maxValue),
      minRaw: selection.allowsEmpty && selection.minRaw === "" ? "" : formatRangeValue(selection.minValue),
    };

    try {
      window.sessionStorage.setItem(key, JSON.stringify(storedSelection));
    } catch (error) {
      // Filters still work without preserving the typed display value.
    }
  };

  const getRangeValues = (group) =>
    Array.from(group.querySelectorAll(rangeValueSelector))
      .map((input) => ({
        input,
        value: toNumber(input.dataset.looseGemsRangeValue),
      }))
      .filter((item) => Number.isFinite(item.value));

  // Avi: every stone's value for this range (carat, length, width, depth), loaded by assets/avi-range-values.js.
  // Shopify's own filter list stops at 100 values, so without this, larger stones can't be selected.
  // Raw strings are kept exactly as Shopify stores them ("1.0", not "1"), otherwise the filter matches nothing.
  const getRangeKey = (group) => group.querySelector(rangeValueSelector)?.name?.split(".").pop() || "";

  const getSourceValues = (group) =>
    ((window.aviRangeValues || {})[getRangeKey(group)] || [])
      .map((raw) => ({ raw: String(raw), value: toNumber(raw) }))
      .filter((item) => Number.isFinite(item.value));

  const getRangeBounds = (group) => {
    const sourceValues = getSourceValues(group);
    const values = sourceValues.length ? sourceValues : getRangeValues(group);
    if (!values.length) return null;

    return values.reduce(
      (bounds, item) => ({
        min: Math.min(bounds.min, item.value),
        max: Math.max(bounds.max, item.value),
      }),
      { min: values[0].value, max: values[0].value }
    );
  };

  const mergeSourceValues = (group) => {
    const sourceValues = getSourceValues(group);
    if (!sourceValues.length) return;

    const paramName = group.querySelector(rangeValueSelector)?.name;
    const valuesContainer = group.querySelector(".loose-gems-range-values");
    if (!paramName || !valuesContainer) return;

    const existingValues = new Set(getRangeValues(group).map(({ value }) => formatRangeValue(value)));
    const activeValues = new Set(
      new URLSearchParams(window.location.search)
        .getAll(paramName)
        .map((value) => formatRangeValue(toNumber(value)))
        .filter(Boolean)
    );
    sourceValues.forEach(({ raw, value: numericValue }) => {
      const normalizedValue = formatRangeValue(numericValue);
      if (!normalizedValue || existingValues.has(normalizedValue)) return;

      const input = document.createElement("input");
      input.className = "loose-gems-range-value";
      input.type = "checkbox";
      input.name = paramName;
      input.value = raw;
      input.hidden = true;
      input.dataset.looseGemsRangeValue = normalizedValue;
      input.tabIndex = -1;
      input.checked = activeValues.has(normalizedValue);

      valuesContainer.append(input);
      existingValues.add(normalizedValue);
    });
  };

  const syncRangeControlBounds = (group, bounds) => {
    if (!bounds) return;

    const min = formatRangeValue(bounds.min);
    const max = formatRangeValue(bounds.max);

    group.querySelectorAll(rangeControlSelector).forEach((control) => {
      control.min = min;
      control.max = max;
    });

    const unit = group.dataset.looseGemsRangeUnit || "";
    const minLabel = unit ? `${min} ${unit}` : min;
    const maxLabel = unit ? `${max} ${unit}` : max;
    const minLimit = group.querySelector('[data-loose-gems-range-limit="min"]');
    const maxLimit = group.querySelector('[data-loose-gems-range-limit="max"]');
    if (minLimit) minLimit.textContent = minLabel;
    if (maxLimit) maxLimit.textContent = maxLabel;
  };

  const getRangeControls = (group, bound) =>
    Array.from(group.querySelectorAll(`[data-loose-gems-range-bound="${bound}"]`)).filter((control) =>
      control.matches(rangeControlSelector)
    );

  const setRangeControlValue = (control, value) => {
    control.value = formatRangeValue(value);
  };

  const updateRangeVisuals = (group, minValue, maxValue) => {
    const bounds = getRangeBounds(group);
    if (!bounds) return;

    const from = bounds.max === bounds.min ? 0 : ((minValue - bounds.min) / (bounds.max - bounds.min)) * 100;
    const to = bounds.max === bounds.min ? 100 : ((maxValue - bounds.min) / (bounds.max - bounds.min)) * 100;
    const slider = group.querySelector(".loose-gems-range-slider");

    if (slider) {
      slider.style.setProperty("--from", `${Math.max(0, Math.min(100, from))}%`);
      slider.style.setProperty("--to", `${Math.max(0, Math.min(100, to))}%`);
    }
  };

  const readRangeSelection = (group) => {
    const bounds = getRangeBounds(group);
    if (!bounds) return null;

    const minInput = group.querySelector('[data-loose-gems-range-input][data-loose-gems-range-bound="min"]');
    const maxInput = group.querySelector('[data-loose-gems-range-input][data-loose-gems-range-bound="max"]');
    const allowsEmpty = group.dataset.looseGemsRangeEmpty === "true";
    const minRaw = minInput?.value?.trim() || "";
    const maxRaw = maxInput?.value?.trim() || "";
    let minValue = minRaw === "" ? bounds.min : toNumber(minRaw);
    let maxValue = maxRaw === "" ? bounds.max : toNumber(maxRaw);

    if (!Number.isFinite(minValue)) minValue = bounds.min;
    if (!Number.isFinite(maxValue)) maxValue = bounds.max;

    minValue = Math.max(bounds.min, Math.min(bounds.max, minValue));
    maxValue = Math.max(bounds.min, Math.min(bounds.max, maxValue));

    if (minValue > maxValue) {
      const swapValue = minValue;
      minValue = maxValue;
      maxValue = swapValue;
    }

    return {
      allowsEmpty,
      bounds,
      maxRaw,
      maxValue,
      minRaw,
      minValue,
    };
  };

  const syncRangeControls = (group, selection, preserveEmpty = false) => {
    if (!selection) return;

    getRangeControls(group, "min").forEach((control) => {
      if (preserveEmpty && selection.allowsEmpty && selection.minRaw === "" && control.matches("[data-loose-gems-range-input]")) {
        control.value = "";
      } else {
        setRangeControlValue(control, selection.minValue);
      }
    });

    getRangeControls(group, "max").forEach((control) => {
      if (preserveEmpty && selection.allowsEmpty && selection.maxRaw === "" && control.matches("[data-loose-gems-range-input]")) {
        control.value = "";
      } else {
        setRangeControlValue(control, selection.maxValue);
      }
    });

    updateRangeVisuals(group, selection.minValue, selection.maxValue);
  };

  const syncStoredRangeControls = (group, storedSelection, fallbackMinValue, fallbackMaxValue) => {
    const bounds = getRangeBounds(group);
    if (!bounds || !storedSelection) return false;

    const allowsEmpty = group.dataset.looseGemsRangeEmpty === "true";
    const minRaw = typeof storedSelection.minRaw === "string" ? storedSelection.minRaw : "";
    const maxRaw = typeof storedSelection.maxRaw === "string" ? storedSelection.maxRaw : "";
    let minValue = minRaw === "" ? fallbackMinValue : toNumber(minRaw);
    let maxValue = maxRaw === "" ? fallbackMaxValue : toNumber(maxRaw);

    if (!Number.isFinite(minValue)) minValue = bounds.min;
    if (!Number.isFinite(maxValue)) maxValue = bounds.max;

    minValue = Math.max(bounds.min, Math.min(bounds.max, minValue));
    maxValue = Math.max(bounds.min, Math.min(bounds.max, maxValue));

    if (minValue > maxValue) {
      const swapValue = minValue;
      minValue = maxValue;
      maxValue = swapValue;
    }

    getRangeControls(group, "min").forEach((control) => {
      if (allowsEmpty && minRaw === "" && control.matches(rangeInputSelector)) {
        control.value = "";
      } else {
        control.value = minRaw === "" ? formatRangeValue(minValue) : minRaw;
      }
    });

    getRangeControls(group, "max").forEach((control) => {
      if (allowsEmpty && maxRaw === "" && control.matches(rangeInputSelector)) {
        control.value = "";
      } else {
        control.value = maxRaw === "" ? formatRangeValue(maxValue) : maxRaw;
      }
    });

    updateRangeVisuals(group, minValue, maxValue);

    return true;
  };

  const applyRangeSelection = (group, commit = false) => {
    const selection = readRangeSelection(group);
    if (!selection) return;

    const rangeValues = getRangeValues(group);
    const clearSelection =
      (selection.allowsEmpty && selection.minRaw === "" && selection.maxRaw === "") ||
      (selection.minValue <= selection.bounds.min && selection.maxValue >= selection.bounds.max);

    rangeValues.forEach(({ input, value }) => {
      input.checked = !clearSelection && !input.disabled && value >= selection.minValue && value <= selection.maxValue;
    });

    syncRangeControls(group, selection, true);
    syncPanelState(group.closest("[data-loose-gems-filter-panel]"));

    if (commit) {
      const form = group.closest(filterFormSelector);
      form?.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  const commitRangeSelection = (group) => {
    clearRangeInputCommit(group);
    group.closest(filterFormSelector)?.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const clearRangeInputCommit = (group) => {
    const timer = rangeInputCommitTimers.get(group);
    if (!timer) return;

    window.clearTimeout(timer);
    rangeInputCommitTimers.delete(group);
  };

  const scheduleRangeInputCommit = (control) => {
    const group = control.closest(rangeGroupSelector);
    if (!group) return;

    clearRangeInputCommit(group);
    rangeInputCommitTimers.set(
      group,
      window.setTimeout(() => {
        const selection = readRangeSelection(group);
        storeRangeSelection(group, selection);
        applyRangeSelection(group, false);
        commitRangeSelection(group);
      }, rangeInputCommitDelay)
    );
  };

  const syncRangeControlInput = (control) => {
    const group = control.closest(rangeGroupSelector);
    if (!group) return null;

    getRangeControls(group, control.dataset.looseGemsRangeBound).forEach((rangeControl) => {
      if (rangeControl !== control) rangeControl.value = control.value;
    });
    applyRangeSelection(group, false);

    return group;
  };

  const hydrateRangeGroup = (group) => {
    mergeSourceValues(group);

    const bounds = getRangeBounds(group);
    if (!bounds) return;

    syncRangeControlBounds(group, bounds);

    const activeValues = getRangeValues(group)
      .filter(({ input }) => input.checked)
      .map(({ value }) => value);

    if (activeValues.length) {
      const minValue = Math.min(...activeValues);
      const maxValue = Math.max(...activeValues);
      const storedSelection = getStoredRangeSelection(group);

      if (!syncStoredRangeControls(group, storedSelection, minValue, maxValue)) {
        getRangeControls(group, "min").forEach((control) => setRangeControlValue(control, minValue));
        getRangeControls(group, "max").forEach((control) => setRangeControlValue(control, maxValue));
        updateRangeVisuals(group, minValue, maxValue);
      }
    } else {
      clearStoredRangeSelection(group);
      if (getSourceValues(group).length) {
        getRangeControls(group, "min").forEach((control) => setRangeControlValue(control, bounds.min));
        getRangeControls(group, "max").forEach((control) => setRangeControlValue(control, bounds.max));
        updateRangeVisuals(group, bounds.min, bounds.max);
      } else {
        const selection = readRangeSelection(group);
        syncRangeControls(group, selection, true);
      }
    }

    syncPanelState(group.closest("[data-loose-gems-filter-panel]"));
  };

  const initRangeFilters = (root = document) => {
    root.querySelectorAll(rangeGroupSelector).forEach(hydrateRangeGroup);
  };

  const syncAccordionToggle = (panel) => {
    const toggle = panel.querySelector(accordionToggleSelector);
    if (!toggle) return;

    toggle.setAttribute("aria-expanded", panel.classList.contains("is-mobile-accordion-open") ? "true" : "false");
  };

  const initMobileAccordions = (root = document) => {
    root.querySelectorAll(accordionSelector).forEach(syncAccordionToggle);
  };

  const toggleMobileAccordion = (toggle) => {
    const panel = toggle.closest(accordionSelector);
    if (!panel) return;

    panel.classList.toggle("is-mobile-accordion-open");
    syncAccordionToggle(panel);
  };

  const clearPanel = (button) => {
    const panel = button.closest("[data-loose-gems-filter-panel]");
    if (!panel) return;

    panel.querySelectorAll(allFilterInputSelector).forEach((input) => {
      input.checked = false;
      syncInputState(input);
    });

    panel.querySelectorAll(rangeGroupSelector).forEach((group) => {
      group.querySelectorAll("[data-loose-gems-range-input]").forEach((input) => {
        input.value = "";
      });
      clearStoredRangeSelection(group);
      applyRangeSelection(group, false);
    });

    syncPanelState(panel);
    panel.closest(filterFormSelector)?.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const initLooseGemsFilters = (root = document) => {
    root.querySelectorAll(carouselSelector).forEach(initCarousel);
    initRangeFilters(root);
    initMobileAccordions(root);
    normalizeSingleSelection(root);
  };

  document.addEventListener(
    "input",
    (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.matches(rangeInputSelector)) {
        event.stopPropagation();
        scheduleRangeInputCommit(event.target);
        return;
      }
      if (event.target.matches(rangeSliderSelector)) {
        event.stopPropagation();
        syncRangeControlInput(event.target);
        return;
      }
      enforceSingleSelection(event.target);
    },
    true
  );
  document.addEventListener(
    "change",
    (event) => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches(rangeControlSelector)) return;

      event.stopPropagation();
      const group = event.target.matches(rangeSliderSelector)
        ? syncRangeControlInput(event.target)
        : event.target.closest(rangeGroupSelector);
      if (event.target.matches(rangeInputSelector) && group) {
        const selection = readRangeSelection(group);
        storeRangeSelection(group, selection);
        applyRangeSelection(group, false);
      }
      if (event.target.matches(rangeSliderSelector) && group) {
        const selection = readRangeSelection(group);
        storeRangeSelection(group, selection);
      }
      if (group) commitRangeSelection(group);
    },
    true
  );
  document.addEventListener("click", (event) => {
    const accordionToggle = event.target.closest(accordionToggleSelector);
    if (accordionToggle) {
      event.preventDefault();
      toggleMobileAccordion(accordionToggle);
      return;
    }

    const clearButton = event.target.closest("[data-loose-gems-clear-panel]");
    if (!clearButton) return;

    event.preventDefault();
    clearPanel(clearButton);
  });
  document.addEventListener("DOMContentLoaded", () => initLooseGemsFilters());
  window.addEventListener("load", () => initLooseGemsFilters());
  document.addEventListener("shopify:section:load", (event) => initLooseGemsFilters(event.target));
  document.addEventListener("loose-gems:filters-rendered", () => initLooseGemsFilters());
  document.addEventListener("avi:range-values", () => initRangeFilters());
})();
